import React from "react";
import styles from "./ResumeTemplateClassic.module.css";
import {
  getJobLocationById,
  getWorkExpText,
  toArray,
} from "../../../../helpers/common";
import {
  getEndData,
  getStartData,
} from "../../../Account/Candidate/MyProfile/ExperienceJourney";
import WebsiteLogo from "../../../../helpers/WebsiteLogo";
import { getCertificationValidity } from "..";

const Header = ({ formData, jobLocations }) => (
  <div className={styles.header}>
    <h1 className={styles.name}>{formData?.name || "Your Name"}</h1>
    <div className={styles.contact}>
      {formData?.location && (
        <>{getJobLocationById(formData?.location, jobLocations)}, India | </>
      )}
      {formData?.email && (
        <>
          <a href={`mailto:${formData.email}`}>{formData.email}</a> |{" "}
        </>
      )}
      {formData?.mobile_number && <>{formData.mobile_number}</>}
    </div>
    {formData?.designation && (
      <div className={styles.title}>{formData?.designation}</div>
    )}
  </div>
);

const Skills = ({ skills }) => {
  const skillsArr = toArray(skills)?.slice(0, 8);
  if (!skillsArr?.length) return null;
  const mid = Math.ceil(skillsArr.length / 2);
  const col1 = skillsArr.slice(0, mid);
  const col2 = skillsArr.slice(mid);
  return (
    <div className={styles.section}>
      <div className={styles.card}>
        <div className={styles.sectionHeader}>KEY SKILLS</div>
        <div className={styles.skillsGrid}>
          <div className={styles.skillColumn}>
            <ul className={styles.bulletList}>
              {col1.map((skill, i) => (
                <li className={styles.skillItem} key={i}>
                  {skill}
                </li>
              ))}
            </ul>
          </div>
          <div className={styles.skillColumn}>
            <ul className={styles.bulletList}>
              {col2.map((skill, i) => (
                <li className={styles.skillItem} key={i}>
                  {skill}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

const Projects = ({ projects }) => {
  if (!projects?.length) return null;
  return (
    <div className={styles.section}>
      <div className={styles.card}>
        <div className={styles.sectionHeader}>PROJECTS</div>
        {projects.map((project, idx) => (
          <div className={styles.projectItem} key={idx}>
            <div className={styles.projectHeader}>
              <h3 className={styles.projectTitle}>
                {project?.title}
                {project?.organization ? ` | ${project.organization}` : ""}
              </h3>
              {project?.date && (
                <span className={styles.date}>{project.date}</span>
              )}
            </div>
            {project?.details && (
              <ul className={styles.bulletList}>
                {toArray(project.details).map((item, i) => (
                  <li className={styles.bulletItem} key={i}>
                    {item}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const Education = ({ educations }) => {
  if (!educations?.length) return null;
  return (
    <div className={styles.section}>
      <div className={styles.card}>
        <div className={styles.sectionHeader}>EDUCATION</div>
        {educations.map((education, idx) => (
          <div className={styles.educationItem} key={idx}>
            <div className={styles.educationHeader}>
              <div>
                <h3 className={styles.degreeTitle}>
                  {education?.additional_fields?.title}
                </h3>
                <span>
                  {education?.additional_fields?.university_college
                    ? `${education?.additional_fields?.university_college}`
                    : ""}
                </span>
              </div>
              <span className={styles.date}>
                {getStartData(education?.additional_fields)} -{" "}
                {getEndData(education?.additional_fields)}
              </span>
            </div>
            <ul className={styles.bulletList}>
              {education?.additional_fields?.certification && (
                <li className={styles.bulletItem}>
                  Certification: {education?.additional_fields?.certification}
                </li>
              )}
              {education?.additional_fields?.gpa && (
                <li className={styles.bulletItem}>
                  GPA: {education?.additional_fields?.gpa}
                </li>
              )}
              {education?.additional_fields?.details &&
                toArray(education?.additional_fields?.details).map(
                  (item, i) => (
                    <li className={styles.bulletItem} key={i}>
                      {item}
                    </li>
                  )
                )}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

const Experience = ({ experience, resumeBuilder }) => {
  if (!experience?.length) return null;
  const totalExperience = resumeBuilder?.total_work_experience
    ? `(${getWorkExpText(resumeBuilder?.total_work_experience)?.trim()})`
    : "";
  return (
    <div className={styles.section}>
      <div className={styles.card}>
        <div className={styles.sectionHeader}>
          WORK EXPERIENCE {totalExperience}
        </div>
        {experience.map((job, idx) => (
          <div className={styles.workItem} key={idx}>
            <div className={styles.workHeader}>
              <h3 className={styles.jobTitle}>
                {job?.additional_fields?.position}
                {job?.additional_fields?.company_name
                  ? ` | ${job?.additional_fields?.company_name}`
                  : ""}
              </h3>
              <span className={styles.date}>
                {getStartData(job?.additional_fields)} -{" "}
                {getEndData(job?.additional_fields)}
              </span>
            </div>

            {job?.additional_fields?.details && (
              <div className={styles.description}>
                {job?.additional_fields?.details}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const Certification = ({ certifications }) => {
  if (!certifications?.length) return null;
  return (
    <div className={styles.section}>
      <div className={styles.card}>
        <div className={styles.sectionHeader}>CERTIFICATIONS</div>
        <ul className={styles.bulletList}>
          {certifications.map((cert, idx) => (
            <li className={styles.awardItem} key={idx}>
              <strong>{cert?.additional_fields?.title}</strong>
              {cert?.additional_fields?.organization && (
                <span className={`ms-2`}>
                  <strong>| {cert?.additional_fields?.organization}</strong>
                </span>
              )}
              
              {cert?.additional_fields?.url && (
                <a
                  href={cert?.additional_fields?.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ms-2"
                >
                  <i className="uil uil-external-link-alt"></i>
                </a>
              )}

              <div style={{ fontSize: "0.95em", color: "#666" }}>
                {getCertificationValidity(cert)}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const Awards = ({ awards }) => {
  if (!awards?.length) return null;
  return (
    <div className={styles.section}>
      <div className={styles.card}>
        <div className={styles.sectionHeader}>AWARDS & ACHIEVEMENTS</div>
        <ul className={styles.bulletList}>
          {awards.map((award, idx) => (
            <li className={styles.awardItem} key={idx}>
              {award}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const ResumeTemplateClassic = ({
  formData,
  jobLocations,
  experiences,
  certifications,
  qualifications,
}) => {
  // Optionally, you can add projects and awards to formData if available
  // For now, fallback to empty array if not present
  const projects = formData?.projects || [];
  const awards = formData?.awards || [];

  return (
    <div className={styles.resume}>
      <Header formData={formData} jobLocations={jobLocations} />

      <div className={styles.container}>
        <Skills skills={formData?.skills} />
        <Projects projects={projects} />
        <Experience experience={experiences} resumeBuilder={formData} />
        <Education educations={qualifications} />
        <Certification certifications={certifications} />
        <Awards awards={awards} />

        {/* Resume Footer */}
        <div className="resume-footer">
          Generated by{" "}
          <a href="https://jobseekerspage.com/" style={{ color: "#888" }}>
            <WebsiteLogo />
          </a>
        </div>
      </div>
    </div>
  );
};

export default ResumeTemplateClassic;
